import { browserAccountStateSessionRepository } from "../account/account-state-session-repository.js";
import { desktopPrivacyApiUrl, desktopPrivacyLocale } from "../account/desktop-privacy-client.js";
import "../components/page-outline.js";
import { renderContentComparison } from "../content-diff.js";
import { contentHistoryComparisonOptions, decodeContentHistory, contentHistorySelection, populateContentVersionControls, readContentResponse, renderContentHistoryNavigation } from "../content-history.js";

const DOCUMENT_MARKER = "[data-desktop-privacy-document]";
const STATIC_ELEMENTS = new Set([
  "a", "abbr", "article", "b", "blockquote", "br", "caption", "code", "dd", "div", "dl", "dt",
  "em", "h1", "h2", "h3", "h4", "h5", "h6", "header", "hr", "i", "li", "nav", "ol", "p",
  "pre", "s", "section", "small", "span", "strong", "sub", "sup", "svg", "table", "tbody",
  "td", "th", "thead", "time", "tr", "u", "ul", "use",
]);
const STATIC_ATTRIBUTES = new Set([
  "id", "class", "title", "lang", "dir", "role", "aria-label", "aria-labelledby", "aria-hidden", "hidden",
  "datetime", "colspan", "rowspan", "scope", "start", "value", "viewBox", "width", "height",
  "data-legal-activity", "data-legal-document-info", "data-legal-participation-disclosure",
]);

function readStaticDocument(html, version) {
  if (!/^sha256-[A-Za-z0-9_-]{43}$/u.test(version ?? "")) {
    throw new TypeError("Desktop privacy notice version is invalid.");
  }
  // Template parsing is inert: full-page scripts and resource-loading elements
  // never enter a live document. Only reviewed static prose elements survive.
  const template = document.createElement("template");
  template.innerHTML = html;
  const matches = template.content.querySelectorAll(DOCUMENT_MARKER);
  if (matches.length !== 1 || matches[0].localName !== "main") {
    throw new TypeError("Desktop privacy notice document is invalid.");
  }
  const main = matches[0];
  for (const formats of main.querySelectorAll("[data-desktop-privacy-formats]")) formats.remove();
  for (const element of [...main.querySelectorAll("*")]) {
    if (!STATIC_ELEMENTS.has(element.localName)) {
      element.remove();
      continue;
    }
    for (const attribute of [...element.attributes]) {
      if (attribute.name === "href" && element.localName === "a") {
        const href = attribute.value;
        const url = new URL(href, window.location.href);
        if (!["https:", "http:", "mailto:"].includes(url.protocol)) element.removeAttribute("href");
      } else if (attribute.name === "href" && element.localName === "use" && /^#fishy-[a-z0-9-]+$/u.test(attribute.value)) {
        // The site owns the inline icon sprite; remote SVG resources are excluded.
      } else if (!STATIC_ATTRIBUTES.has(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    }
  }
  const ids = new Set();
  for (const element of main.querySelectorAll("[id]")) {
    if (!element.id || ids.has(element.id) || document.getElementById(element.id)) {
      throw new TypeError("Desktop privacy notice anchors are invalid.");
    }
    ids.add(element.id);
  }
  if (main.querySelectorAll("h1").length !== 1 || main.querySelectorAll("h2").length === 0) {
    throw new TypeError("Desktop privacy notice headings are invalid.");
  }
  const fragment = document.createDocumentFragment();
  fragment.append(...main.childNodes);
  return fragment;
}

function mountReader(root) {
  const lifetime = new AbortController();
  const language = window.__fishystuffLanguage;
  const session = browserAccountStateSessionRepository();
  const content = root.querySelector("[data-reader-document]");
  const reading = root.querySelector("[data-reader-reading]");
  const intro = root.querySelector("[data-reader-intro]");
  const statePanel = root.querySelector("[data-reader-state]");
  const status = root.querySelector("[data-reader-status]");
  const spinner = root.querySelector("[data-reader-spinner]");
  const signIn = root.querySelector("[data-reader-sign-in]");
  const retry = root.querySelector("[data-reader-retry]");
  const outline = root.querySelector("[data-reader-outline]");
  const rss = root.querySelector("[data-reader-rss]");
  const text = root.querySelector("[data-reader-text]");
  let request = null;
  let locale = null;
  let languageReady = false;

  function clear() {
    request?.abort();
    request = null;
    outline.contentElement = null;
    content.replaceChildren();
    reading.querySelector("[data-content-history]")?.remove();
    content.removeAttribute("lang");
    rss.removeAttribute("href");
    text.removeAttribute("href");
    reading.hidden = true;
    intro.hidden = false;
    root.removeAttribute("data-reader-version");
  }

  function present(state) {
    document.title = language.t("desktop.privacy.reader.title", {}, { locale: locale ?? desktopPrivacyLocale() });
    root.dataset.readerState = state;
    const key = `desktop.privacy.reader.${state}`;
    status.setAttribute("data-i18n-text", key);
    status.textContent = language.t(key, {}, { locale: locale ?? desktopPrivacyLocale() });
    statePanel.hidden = state === "ready";
    spinner.hidden = state !== "loading";
    signIn.hidden = state !== "signed_out";
    if (!signIn.hidden) signIn.href = `${locale === "de-DE" ? "/de-DE" : ""}/account/`;
    else signIn.removeAttribute("href");
    retry.hidden = !["denied", "error", "signed_out"].includes(state);
    root.setAttribute("aria-busy", String(state === "loading"));
  }


  async function load() {
    clear();
    if (lifetime.signal.aborted) return;
    present("loading");
    if (!languageReady) return;
    const authority = session.state();
    if (authority.kind === "signedOut") {
      present("signed_out");
      return;
    }
    if (!["ready", "unavailable"].includes(authority.kind)) return;
    // Ordinary session discovery can reject current Terms. Only this endpoint
    // decides notice eligibility; Terms rejection must still reach it.
    const current = new AbortController();
    request = current;
    try {
      const historyResponse = await fetch(desktopPrivacyApiUrl("history"), {
        method: "GET", cache: "no-store", credentials: "include", redirect: "error",
        headers: { Accept: "application/json" }, signal: current.signal,
      });
      const response = historyResponse;
      if (current.signal.aborted) return;
      if (response.status === 401 || response.status === 403) {
        await response.body?.cancel();
        if (!current.signal.aborted) present(response.status === 401 ? "signed_out" : "denied");
        return;
      }
      const canonicalPath = locale === "de-DE" ? "/de-DE/desktop/privacy/" : "/desktop/privacy/";
      const manifest = decodeContentHistory(JSON.parse(await readContentResponse(response, "application/json")), {
        locale, canonicalPath, privateNotice: true,
      });
      const selection = contentHistorySelection(manifest, window.location.search);
      const { selected, baseline, view, displayed } = selection;
      if (!selected) throw new TypeError("No Desktop notice has been approved.");
      const readVersion = async (format, version, type) => {
        const response = await fetch(desktopPrivacyApiUrl(format, version), {
          cache: "no-store", credentials: "include", redirect: "error", headers: { Accept: type }, signal: current.signal,
        });
        if (response.headers.get("x-fishystuff-notice-version") !== version) {
          await response.body?.cancel();
          throw new TypeError("Desktop privacy response version does not match the selection.");
        }
        return readContentResponse(response, type);
      };
      let fragment;
      if (view === "diff") {
        const [before, after] = await Promise.all([
          readVersion("html", baseline.version, "text/html"),
          readVersion("html", selected.version, "text/html"),
        ]);
        if (current.signal.aborted) return;
        fragment = renderContentComparison(document,
          readStaticDocument(before, baseline.version), readStaticDocument(after, selected.version), locale, contentHistoryComparisonOptions(manifest));
      } else {
        const html = await readVersion("html", displayed.version, "text/html");
        if (current.signal.aborted) return;
        fragment = readStaticDocument(html, displayed.version);
      }
      if (current.signal.aborted) return;
      const controls = document.createElement("template");
      controls.innerHTML = renderContentHistoryNavigation(manifest);
      reading.prepend(controls.content);
      populateContentVersionControls(reading.querySelector("[data-content-history]"), manifest, selection);
      content.append(fragment);
      content.lang = locale;
      rss.href = desktopPrivacyApiUrl("rss");
      text.href = desktopPrivacyApiUrl("text", displayed.version);
      reading.hidden = false;
      intro.hidden = true;
      root.dataset.readerVersion = displayed.version;
      present("ready");
      outline.contentElement = content;
    } catch (error) {
      if (!current.signal.aborted) {
        clear();
        present("error");
        globalThis.reportError?.(error);
      }
    } finally {
      if (request === current) request = null;
    }
  }

  const unsubscribe = session.subscribe(() => { void load(); });
  void session.load();
  window.addEventListener(language.event, () => {
    const next = desktopPrivacyLocale();
    if (next === locale) return;
    locale = next;
    void load();
  }, { signal: lifetime.signal });
  retry.addEventListener("click", async () => {
    clear();
    present("loading");
    await session.refresh();
    if (!lifetime.signal.aborted) void load();
  }, { signal: lifetime.signal });
  void Promise.resolve(language.ready).then(() => {
    if (lifetime.signal.aborted) return;
    languageReady = true;
    locale = desktopPrivacyLocale();
    void load();
  }).catch((error) => {
    if (lifetime.signal.aborted) return;
    clear();
    present("error");
    globalThis.reportError?.(error);
  });
  return () => {
    lifetime.abort();
    unsubscribe();
    clear();
    present("loading");
  };
}

const root = document.querySelector("[data-desktop-privacy-reader]");
if (root) {
  let dispose = mountReader(root);
  window.addEventListener("pagehide", () => { dispose(); });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) dispose = mountReader(root);
  });
}
